import { Box, Card, CardContent, LinearProgress, Typography, Chip, Alert } from '@mui/material';
import { CheckCircle, TrendingUp, EmojiEvents, Today, Close, Star } from '@mui/icons-material';
import { ChallengeProgress as ChallengeProgressType, ChallengeStatus } from 'types/challenge';
import { Pack } from 'types/pack';
import { makeStyles } from 'tss-react/mui';
import StoreContext from 'contexts/storeContext';
import { useContext } from 'react';
import { isCurrentOpeningHour, dateToBackendDateString } from 'utils';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    flexWrap: 'wrap',
  },
  card: {
    flex: 1,
    minWidth: 300,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
    border: `1px solid ${theme.palette.primary.main}40`,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  progressContainer: {
    marginTop: theme.spacing(2),
  },
  daysContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  dayChip: {
    fontWeight: 600,
  },
  alert: {
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
}));

interface Props {
  challengeProgress: ChallengeProgressType;
  packs: Pack[];
}

export default function ChallengeProgress({ challengeProgress, packs }: Props) {
  const { classes } = useStyles();
  const { store } = useContext(StoreContext);

  const { weekly_pack_challenge, promotion_challenge } = challengeProgress;

  // Calculate progress percentage for weekly pack challenge
  const weeklyPackRequiredDays = 2;
  const weeklyPackDaysMetCount = weekly_pack_challenge.days_completed.length;
  const weeklyPackProgress = Math.min((weeklyPackDaysMetCount / weeklyPackRequiredDays) * 100, 100);

  // Determine the "working day" date: if store is open, use today; if closed, use tomorrow
  // This accounts for the fact that after closing time, we're accumulating for the next working day
  // Using local timezone to avoid UTC/local mismatches (e.g., store closes at 11pm local)
  const isStoreOpen = isCurrentOpeningHour(store.next_dated_opening_hour);
  const workingDate = new Date();
  if (!isStoreOpen) {
    workingDate.setDate(workingDate.getDate() + 1); // Use tomorrow if store is closed
  }
  const today = dateToBackendDateString(workingDate); // 'YYYY-MM-DD' in local timezone

  const isWeeklyPacksCompletedToday = weekly_pack_challenge.days_completed.some((day) => day === today);

  // Calculate progress for promotion challenge
  const promotionDaysCount = promotion_challenge.current_week.days_completed.length;
  const promotionRequiredDays = 3;
  const promotionProgress = Math.min((promotionDaysCount / promotionRequiredDays) * 100, 100);

  // Check previous week status
  const previousWeekCompleted = promotion_challenge.previous_week?.status === ChallengeStatus.COMPLETED;

  // Check if promotion challenge was completed today
  const isPromotionCompletedToday = promotion_challenge.current_week.days_completed.some(day => day === today);

  // Calculate today's packs and items from raw_data (defensively handle missing data)
  const todayPromotionData = promotion_challenge.current_week.raw_data?.[today] ?? {};
  const todayPromotionPacks = Object.entries(todayPromotionData).filter(([, itemCount]) => itemCount > 0).length;
  const todayPromotionItems = Object.values(todayPromotionData)
    .filter((itemCount) => itemCount > 0)
    .reduce((sum, count) => sum + count, 0);

  return (
    <Box className={classes.container}>
      {/* Weekly Pack Challenge Card */}
      <Card className={classes.card} elevation={2}>
        <CardContent>
          <Box className={classes.cardHeader}>
            <EmojiEvents color="primary" fontSize="large" />
            <Typography variant="h6" fontWeight="bold">
              Desafío Semanal de Packs
            </Typography>
          </Box>

          {weekly_pack_challenge.status === ChallengeStatus.COMPLETED && !weekly_pack_challenge.notification_sent && (
            <Alert
              severity="success"
              className={classes.alert}
            >
              ¡Desafío completado! Pronto notificaremos a los usuarios cercanos de tus packs.
            </Alert>
          )}

          {weekly_pack_challenge.notification_sent && (
            <Alert
              severity="info"
              className={classes.alert}
            >
              ¡Notificaciones enviadas! Los usuarios cercanos han sido notificados de tus nuevos packs.
            </Alert>
          )}

          {weekly_pack_challenge.status !== ChallengeStatus.COMPLETED && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Publica 3 o más packs, cada uno con al menos 4 ítems, en 2 días distintos dentro de la misma semana.<br />
              Al cumplirlo, notificaremos a los usuarios cercanos a tu tienda!
            </Typography>
          )}

          {weekly_pack_challenge.status !== ChallengeStatus.COMPLETED && (
            <Box className={classes.progressContainer}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="600">
                    Días completado esta semana: {weeklyPackDaysMetCount}/{weeklyPackRequiredDays}
                  </Typography>
                  {isWeeklyPacksCompletedToday && (
                    <Chip
                      icon={<Today />}
                      label="¡Hoy!"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                </Box>
                <Chip
                  label="En progreso"
                  color="warning"
                  size="small"
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={weeklyPackProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />

              {!isWeeklyPacksCompletedToday &&
                weekly_pack_challenge.raw_data?.[today] &&
                Object.keys(weekly_pack_challenge.raw_data[today]).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="600" gutterBottom>
                      Progreso de hoy:
                    </Typography>
                    {Object.entries(weekly_pack_challenge.raw_data[today] ?? {})
                      .filter(([, itemCount]) => itemCount > 0)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 3)
                      .map(([packId, itemCount]) => {
                        const itemProgress = (itemCount / 4) * 100;
                        const meetsRequirement = itemCount >= 4;
                        const pack = packs.find((p) => p.id === Number(packId));
                        const packName = pack?.name || `Pack #${packId}`;
                        return (
                          <Box key={packId} sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="body2" fontSize="0.875rem">
                                {packName}
                              </Typography>
                              <Typography variant="caption" color={meetsRequirement ? 'success.main' : 'text.secondary'} fontWeight="600">
                                {itemCount}/4 items
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(itemProgress, 100)}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: meetsRequirement ? 'success.main' : 'warning.main',
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                  </Box>
                )}

              {weeklyPackDaysMetCount > 0 && (
                <Box className={classes.daysContainer}>
                  <Typography variant="caption" color="text.secondary">
                    Días completado esta semana:
                  </Typography>
                  {weekly_pack_challenge.days_completed.map((day) => {
                    const isDayToday = day === today;
                    return (
                      <Chip
                        key={day}
                        icon={<CheckCircle />}
                        label={new Date(day).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                        color="success"
                        size="small"
                        className={classes.dayChip}
                        sx={isDayToday ? { fontWeight: 800, border: '2px solid', borderColor: 'success.main' } : {}}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Promotion Challenge Card */}
      <Card className={classes.card} elevation={2}>
        <CardContent>
          <Box className={classes.cardHeader}>
            <TrendingUp color="primary" fontSize="large" />
            <Typography variant="h6" fontWeight="bold">
              Desafío de Tiendas Destacadas
            </Typography>
          </Box>

          {store.is_promoted && (
            <Alert
              icon={<Star sx={{ color: '#FFD700' }} />}
              severity="success"
              className={classes.alert}
            >
              ¡Felicitaciones! Tu tienda está destacada. Sigue cumpliendo el desafío cada semana para mantener este beneficio.
            </Alert>
          )}

          {!store.is_promoted && previousWeekCompleted && promotion_challenge.current_week.status === ChallengeStatus.COMPLETED && (
            <Alert
              icon={<Star sx={{ color: '#FFD700' }} />}
              severity="success"
              className={classes.alert}
              sx={{ fontWeight: 600 }}
            >
              🎉 ¡Increíble! Has completado el desafío por 2 semanas consecutivas. Tu tienda ahora aparecerá destacada en el inicio de la app.
            </Alert>
          )}

          {!store.is_promoted && !previousWeekCompleted && promotion_challenge.current_week.status === ChallengeStatus.COMPLETED && (
            <Alert
              severity="success"
              className={classes.alert}
            >
              ¡Excelente! Has completado el desafío de esta semana. Complétalo la próxima semana también para convertirte en tienda destacada.
            </Alert>
          )}

          {promotion_challenge.current_week.status !== ChallengeStatus.COMPLETED && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Publica 3 packs con 10 ítems en total, en 3 días distintos dentro de la misma semana.<br />
              Al cumplirlo por 2 semanas consecutivas, tu tienda se convertirá en tienda promovida y aparecerá destacada en el inicio
              de la app
            </Typography>
          )}

          {promotion_challenge.current_week.status !== ChallengeStatus.COMPLETED && (
            <Box className={classes.progressContainer}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="600">
                    Días completado esta semana: {promotionDaysCount}/{promotionRequiredDays}
                  </Typography>
                  {isPromotionCompletedToday && (
                    <Chip
                      icon={<Today />}
                      label="¡Hoy!"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                </Box>
                <Chip
                  label="En progreso"
                  color="warning"
                  size="small"
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={promotionProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />

              {!isPromotionCompletedToday && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600" gutterBottom>
                    Progreso de hoy:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" fontSize="0.875rem">
                        Packs publicados
                      </Typography>
                      <Typography
                        variant="caption"
                        color={todayPromotionPacks >= 3 ? 'success.main' : 'text.secondary'}
                        fontWeight="600"
                      >
                        {todayPromotionPacks}/3
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((todayPromotionPacks / 3) * 100, 100)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: todayPromotionPacks >= 3 ? 'success.main' : 'warning.main',
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" fontSize="0.875rem">
                        Items publicados
                      </Typography>
                      <Typography
                        variant="caption"
                        color={todayPromotionItems >= 10 ? 'success.main' : 'text.secondary'}
                        fontWeight="600"
                      >
                        {todayPromotionItems}/10
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((todayPromotionItems / 10) * 100, 100)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: todayPromotionItems >= 10 ? 'success.main' : 'warning.main',
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}

              <Box className={classes.daysContainer}>
                {promotionDaysCount > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Días completado esta semana:
                    </Typography>
                    {promotion_challenge.current_week.days_completed.map((day) => {
                      const isDayToday = day === today;
                      return (
                        <Chip
                          key={day}
                          icon={<CheckCircle />}
                          label={new Date(day).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                          color="success"
                          size="small"
                          className={classes.dayChip}
                          sx={isDayToday ? { fontWeight: 800, border: '2px solid', borderColor: 'success.main' } : {}}
                        />
                      );
                    })}
                    <Box sx={{ width: '1px', height: '24px', backgroundColor: 'divider', mx: 1 }} />
                  </>
                )}
                <Typography variant="caption" color="text.secondary">
                  Desafío de la semana pasada:
                </Typography>
                {previousWeekCompleted ? (
                  <Chip
                    icon={<CheckCircle />}
                    label="Completado"
                    color="success"
                    size="small"
                    className={classes.dayChip}
                  />
                ) : (
                  <Chip
                    icon={<Close />}
                    label="Incompleto"
                    size="small"
                    className={classes.dayChip}
                    sx={{
                      backgroundColor: '#ffebee',
                      color: '#d32f2f',
                      '& .MuiChip-icon': {
                        color: '#d32f2f',
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
